'use client'
import { useQuery, useSubscription } from '@urql/next'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { graphql } from '@/generated/gql'
import { IntegrationStatus } from '@/generated/gql/graphql'
import { SUBSCRIBE_INTEGRATION_INSTALLED } from '@/services/integrations'

const GET_INTEGRATIONS = graphql(`
    query GetIntegrations($where: AppFilterInput!) {
        appDirectory {
            apps(where: $where) {
                id
                status
                categories
                isAuthorized
                isAdministrative
                metadata {
                    name
                    smallLogo
                    learnMoreUrl
                }
            } 
        } 
    }
`)

export const GET_TEAMS_SETTINGS = graphql(`
    query GetTeamsSettings{
        teamsSettings {
            enableAutomaticRecording
        }
    }
`)

export const GET_ZOOM_SETTINGS = graphql(`
    query GetZoomSettings{
        zoomSettings {
            usePmi
            enableAutomaticRecording
        }
    }
`)

const useRefetchIntegration = () => {
    const [, getConnectedIntegrations] = useQuery({
        query: GET_INTEGRATIONS,
        variables: {
            where: {
                status: { eq: IntegrationStatus.Connected },
            },
        },
        pause: true,
    })

    return getConnectedIntegrations
}

const useConnectedIntegrations = () => {
    const [{ data, fetching, error }] = useQuery({
        query: GET_INTEGRATIONS,
        variables: {
            where: {
                status: { eq: IntegrationStatus.Connected },
            },
        },
        requestPolicy: 'cache-and-network',
        context: useMemo(() => ({ suspense: true }), []),
    })

    return { data, fetching, error }
}

const useConnectedCalendars = () => {
    const [{ data, fetching, error }] = useQuery({
        query: GET_INTEGRATIONS,
        variables: {
            where: {
                status: { eq: IntegrationStatus.Connected },
                categories: { some: { eq: 'Calendar' } },
            },
        },
        requestPolicy: 'cache-and-network',
        context: useMemo(() => ({ suspense: true }), []),
    })

    return { data, fetching, error }
}

const useZoomSettings = () => {
    const [{ data, fetching, error }] = useQuery({
        query: GET_ZOOM_SETTINGS,
        requestPolicy: 'cache-and-network',
        context: useMemo(() => ({ suspense: true }), []),
    })

    return { data, fetching, error }
}

const useTeamsettings = () => {
    const [{ data, fetching, error }] = useQuery({
        query: GET_TEAMS_SETTINGS,
        requestPolicy: 'cache-and-network',
        context: useMemo(() => ({ suspense: true }), []),
    })

    return { data, fetching, error }
}

const IntegrationsProvider = ({ children }: { children: ReactNode }) => {
    const refetchIntegration = useRefetchIntegration()
    useSubscription(
        {
            query: SUBSCRIBE_INTEGRATION_INSTALLED,
        },
        (_, result) => {
            refetchIntegration()
            return result
        },
    )

    return children
}

export {
    useRefetchIntegration,
    useConnectedIntegrations,
    useConnectedCalendars,
    useZoomSettings,
    useTeamsettings,
    IntegrationsProvider,
}
